import { Input, Modal, Space, Spin, Typography } from '@yimoka/antd';
import { observer, useField, useInitStore } from '@yimoka/react';
import type { IHTTPResponse } from '@yimoka/shared';
import { useEffect, useState } from 'react';

import { authAccountGetPhoneCaptcha } from '@/entities/auth-account';
import type { IAuthAccountPhoneCaptchaParams } from '@/entities/auth-account.d';
import { useTranslation } from '@/locales/i18n';

import { ImageCaptcha } from './captcha-image';

export interface PhoneCaptchaButtonProps {
  phone?: string;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  text?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  onNeedCaptcha?: () => void;
}

interface PhoneCaptchaFeedback {
  onError?: (error: unknown) => void;
  onNeedCaptcha?: () => void;
  onSuccess?: () => void;
  setCountdown: (value: number) => void;
  setNeedCaptcha: (value: boolean) => void;
}

interface PhoneCaptchaLinkProps {
  buttonText: string;
  className?: string;
  dataTestId?: string;
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
}

interface PhoneCaptchaModalProps {
  captchaCode: string;
  needCaptcha: boolean;
  onCancel: () => void;
  onCaptchaCodeChange: (value: string) => void;
  onCaptchaIDChange: (value: string) => void;
  onGetCode: () => void;
}

function createPhoneCaptchaFeedback(feedback: PhoneCaptchaFeedback) {
  return {
    notify: true,
    failRun: (res: Partial<IHTTPResponse>) => {
      if (res.metadata?.needCaptcha) {
        feedback.setNeedCaptcha(true);
        feedback.onNeedCaptcha?.();
      }
      feedback.onError?.(res);
    },
    successRun: () => {
      feedback.setNeedCaptcha(false);
      feedback.setCountdown(60);
      feedback.onSuccess?.();
    },
  };
}

function PhoneCaptchaLink(props: PhoneCaptchaLinkProps) {
  const { buttonText, className, dataTestId, disabled, loading, onClick } = props;

  return (
    <Typography.Link
      className={className}
      data-testid={dataTestId}
      disabled={disabled}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
      onClick={onClick}
    >
      {loading ? <Spin size="small" style={{ marginRight: 4 }} /> : null}
      {buttonText}
    </Typography.Link>
  );
}

function PhoneCaptchaModal(props: PhoneCaptchaModalProps) {
  const { t } = useTranslation();
  const {
    captchaCode,
    needCaptcha,
    onCancel,
    onCaptchaCodeChange,
    onCaptchaIDChange,
    onGetCode,
  } = props;

  return (
    <Modal
      mask={{ closable: false }}
      okButtonProps={{ disabled: captchaCode.length < 4 }}
      open={needCaptcha}
      title={t('Account:captcha.imageTitle')}
      trigger={false}
      width={300}
      onCancel={onCancel}
      onOk={onGetCode}
    >
      <Space>
        <Input
          maxLength={4}
          value={captchaCode}
          onChange={onCaptchaCodeChange}
          onPressEnter={() => captchaCode.length === 4 && onGetCode()}
        />
        <ImageCaptcha onChange={onCaptchaIDChange} />
      </Space>
    </Modal>
  );
}

export const PhoneCaptchaButton = observer((props: PhoneCaptchaButtonProps) => {
  const { t } = useTranslation();
  const {
    phone,
    onSuccess,
    onError,
    text = t('Account:login.sendCode'),
    disabled = false,
    className,
    'data-testid': dataTestId,
    onNeedCaptcha,
  } = props;
  const field = useField();
  const [countdown, setCountdown] = useState(0);
  const [needCaptcha, setNeedCaptcha] = useState(false);
  const phoneValue = phone ?? field?.form?.values?.phone ?? '';
  const phonePrefixValue = String(field?.form?.values?.phonePrefix ?? '86').replace(/^\+/, '');

  const { values, setValues, fetch, loading } = useInitStore<IAuthAccountPhoneCaptchaParams>({
    defaultValues: {
      phone: phoneValue,
      phonePrefix: phonePrefixValue,
      captchaID: '',
      captchaCode: '',
    },
    options: { filterBlankAtRun: true },
    api: authAccountGetPhoneCaptcha,
    afterAtFetch: createPhoneCaptchaFeedback({
      onError,
      onNeedCaptcha,
      onSuccess,
      setCountdown,
      setNeedCaptcha,
    }),
  });

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    setValues({
      phone: phoneValue,
      phonePrefix: phonePrefixValue,
    });
  }, [setValues, phoneValue, phonePrefixValue]);

  const { captchaCode = '' } = values;
  const getCode = () => {
    void fetch();
  };
  const isValidPhone = typeof phoneValue === 'string' && /^1[3-9]\d{9}$/.test(phoneValue);
  const isDisabled = loading || !isValidPhone || countdown > 0 || disabled;
  const buttonText = countdown > 0 ? t('Account:captcha.retryAfter', { seconds: countdown }) : text;

  return (
    <>
      <PhoneCaptchaLink
        buttonText={buttonText}
        className={className}
        dataTestId={dataTestId}
        disabled={isDisabled}
        loading={loading}
        onClick={getCode}
      />

      <PhoneCaptchaModal
        captchaCode={captchaCode}
        needCaptcha={needCaptcha}
        onCancel={() => setNeedCaptcha(false)}
        onCaptchaCodeChange={value => setValues({ captchaCode: value })}
        onCaptchaIDChange={value => setValues({ captchaID: value })}
        onGetCode={getCode}
      />
    </>
  );
});
